import { useEffect, useRef } from 'react';
import { useRouterState } from '@tanstack/react-router';

import type { EventContent } from './envelope';
import { track } from './track';

const IS_INSTALLED_KEY = 'pwa-installed';

/**
 * Records `screen_view` once per path, when the page says it is ready
 * (`enabled`) and after a short delay, so a route the reader only passes
 * through is not counted and the page has settled (its document title is
 * what a destination reports). `content` names the issue and article the
 * route shows, when it shows one.
 */
export function useScreenView({
	enabled,
	content,
	delay = 500,
}: {
	enabled: boolean;
	content?: EventContent;
	delay?: number;
}): void {
	const path = useRouterState({ select: (state) => state.location.pathname });
	const screen = useRouterState({
		select: (state): string => {
			const match = state.matches[state.matches.length - 1];
			return String(
				match?.fullPath ?? match?.routeId ?? state.location.pathname,
			);
		},
	});

	// Read when the event fires, not when the timer starts: callers build
	// `content` from data that is still loading, as a new object each render,
	// and neither may restart the timer.
	const latest = useRef({ content, screen });
	latest.current = { content, screen };
	const lastTracked = useRef<string | null>(null);

	useEffect(() => {
		if (!enabled || !path || lastTracked.current === path) {
			return;
		}
		const timeout = setTimeout(() => {
			if (lastTracked.current === path) {
				return;
			}
			lastTracked.current = path;
			track(
				'screen_view',
				{ screen: latest.current.screen, path },
				latest.current.content,
			);
		}, delay);
		return () => clearTimeout(timeout);
	}, [path, enabled, delay]);
}

/**
 * Records `app_install` the first time the app runs installed on this
 * device. `isInstalled` is passed in: it lives in the `app/` layer's context,
 * which `shared/` may not import.
 */
export function useAppInstallEvent(isInstalled: boolean): void {
	useEffect(() => {
		if (!isInstalled || localStorage.getItem(IS_INSTALLED_KEY)) {
			return;
		}
		localStorage.setItem(IS_INSTALLED_KEY, 'true');
		track('app_install', {});
	}, [isInstalled]);
}
